import { ParsedUrlQuery } from 'querystring';

import { useState } from 'react';
import FsLightbox from 'fslightbox-react';
import { GetStaticPaths, GetStaticPathsResult, GetStaticProps, GetStaticPropsResult } from 'next';
import Link from 'next/link';
import styled from 'styled-components';
import tw from 'twin.macro';
import resolveConfig from 'tailwindcss/resolveConfig';

import { event } from '@app/lib/facebook-pixel';
import { firebase } from '@app/config/fire-conf';
import Layout from '@app/layouts/page-default';
import MovieDetailSlide from '@app/components/movie-detail-slide';
import Gallery from '@app/components/gallery';
import Cast from '@app/components/casts';
import MovieDetails from '@app/components/movie-details';
import CinemaCard from '@app/components/cinema-card';
import { Cinema, CinemaSingle, Movie, MovieSingle, MovieStatus } from '@app/types';
import SEO from '@app/components/seo';
import { getAvailableCinemas } from '@app/utils';

import tailwindConfig from '../../tailwind.config';

const fullConfig = resolveConfig(tailwindConfig);

export const MovieDetailContentBlockStyled = styled.div`
  ${tw`flex mx-auto z-10 w-full justify-between p-5 lg:px-5 relative`}

  .content-container {
    ${tw`flex w-full flex-col lg:flex-row justify-between overflow-hidden mx-auto`}
    max-width: 1360px;

    @media (min-width: ${fullConfig.theme.screens['2xl']}) {
      ${tw`px-8`}
    }
  }

  .movie-sidebar {
    ${tw`flex-col flex relative z-0 px-6 lg:px-0 lg:pr-0 w-full md:flex-row lg:flex-col justify-between lg:justify-start`}
    width: 100%;

    @media (min-width: ${fullConfig.theme.screens.lg}) {
      max-width: 224px;
    }

    @media (min-width: ${fullConfig.theme.screens['2xl']}) {
      ${tw`md:max-w-xs`}
      width: 306px;
    }

    .btn {
      ${tw`w-full`}
      @media (min-width: ${fullConfig.theme.screens.md}) {
        width: 47%;
      }

      @media (min-width: ${fullConfig.theme.screens.lg}) {
        ${tw`w-full`}
      }
    }
  }

  .movie-details {
    ${tw`flex-auto pt-6 pb-12 z-0 flex-col flex justify-end px-6`};

    h2 {
      ${tw`text-white text-3xl lg:text-5xl font-bold pb-2 w-full`};
    }

    p {
      ${tw`text-base text-white font-normal mb-3`};
      max-width: 700px;
    }
  }

  .cinema-wrapper {
    max-width: 856px;
  }
`;

type MovieSingleProps = {
  movieStrObj: string;
  cinemasStrObjs: string[];
};

export default function MovieSinglePage({
  movieStrObj,
  cinemasStrObjs,
}: MovieSingleProps): JSX.Element {
  const [trailerToggler, setTrailerToggler] = useState(false);
  const [lightboxController, setLightboxController] = useState({
    toggler: false,
    slide: 1,
  });
  const movieSingle = JSON.parse(movieStrObj) as MovieSingle;
  const movie: Movie = {
    ...movieSingle,
    releaseDate: firebase.firestore.Timestamp.fromMillis(movieSingle.releaseDate.seconds * 1000),
    lastUpdatedAt: firebase.firestore.Timestamp.fromMillis(
      movieSingle.lastUpdatedAt.seconds * 1000,
    ),
    premiereDate: movieSingle.premiereDate
      ? firebase.firestore.Timestamp.fromMillis(movieSingle.premiereDate.seconds * 1000)
      : undefined,
  };
  const cinemas: Cinema[] = cinemasStrObjs.map((cinemaStrObj) => {
    const cinemaSingle = JSON.parse(cinemaStrObj) as CinemaSingle;
    const cinema: Cinema = {
      ...cinemaSingle,
      lastUpdatedAt: firebase.firestore.Timestamp.fromMillis(
        cinemaSingle.lastUpdatedAt.seconds * 1000,
      ),
    };

    return cinema;
  });
  const shareUrl = () => {
    const id = new Date().getTime();
    event('share', {
      type: 'facebook',
      url: window.location.href,
      title: window.document.title,
    });
    window.open(
      `https://www.facebook.com/sharer.php?u=${encodeURIComponent(window.location.href)}&t=${
        window.document.title
      }`,
      id.toString(),
      'toolbar=0,scrollbars=1,location=0,statusbar=0,menubar=0,resizable=1,width=600,height=400',
    );
  };

  const showTrailer = () => {
    setTrailerToggler(!trailerToggler);
  };

  const showGallery = (id: number) => {
    setLightboxController({
      toggler: !lightboxController.toggler,
      slide: id,
    });
  };

  return (
    <Layout>
      <SEO title={movie?.name} />
      {movie && <MovieDetailSlide movie={movie} link={false} />}

      <MovieDetailContentBlockStyled>
        <div className="content-container">
          <div className="movie-sidebar">
            {movie.status === MovieStatus.active && (
              <Link href={`/buy-tickets?movie=${movie.slug}`}>
                <a className="btn btn--primary mb-10 md:mb-0 lg:mb-10 lg:mt-4">Buy tickets</a>
              </Link>
            )}
            <button
              type="button"
              onClick={() => shareUrl()}
              className="btn btn--secondary-outline md:mb-0">
              Share
            </button>
          </div>
          <div className="movie-details">
            {movie?.photos && (
              <Gallery
                data={movie?.photos}
                title="Photos &amp; Videos"
                openGallery={(id: number) => showGallery(id)}
                trailer={movie?.trailer}
                showTrailer={() => showTrailer()}
              />
            )}
            {movie?.cast && <Cast title="Cast" cast={movie?.cast} />}

            <MovieDetails
              title="Details"
              crew={movie?.crew}
              language={movie?.language}
              releaseDate={firebase.firestore.Timestamp.fromMillis(
                movie?.releaseDate.seconds * 1000,
              )}
            />

            <div className="cinema-wrapper grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-6 pb-14 pr-8">
              {cinemas.map((cinema) => (
                <CinemaCard key={cinema.label} cinemaName={cinema.label} cinema={cinema} />
              ))}
            </div>
          </div>
        </div>
      </MovieDetailContentBlockStyled>
      <FsLightbox toggler={trailerToggler} sources={[movie.trailer]} />

      <FsLightbox
        toggler={lightboxController.toggler}
        sources={movie.photos?.map((item) => item.url)}
        slide={lightboxController.slide}
        type="image"
      />
    </Layout>
  );
}

export const getStaticPaths: GetStaticPaths = async (): Promise<GetStaticPathsResult> => {
  const moviesQuerySnapshot = await firebase.firestore().collection('movies').get();

  const paths: { params: ParsedUrlQuery }[] = [];
  moviesQuerySnapshot.forEach((movieDoc) => {
    paths.push({
      params: {
        slug: (movieDoc.data() as Movie).slug,
      },
    });
  });

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<MovieSingleProps> = async ({ params }) => {
  let returnMovie: GetStaticPropsResult<MovieSingleProps> = {
    notFound: true,
  };
  if (params) {
    const movieQuerySnap = await firebase
      .firestore()
      .collection('movies')
      .where('status', '!=', MovieStatus.archive)
      .where('slug', '==', params.slug)
      .get();

    if (movieQuerySnap.docs.length === 1) {
      const movie: Movie = {
        ...(movieQuerySnap.docs[0].data() as Movie),
        id: movieQuerySnap.docs[0].id,
      };
      const movieStrObj = JSON.stringify(movie);

      const cinemasQuerySnap = await firebase.firestore().collection('cinemas').get();

      const cinemasStrObjs: string[] = [];
      cinemasQuerySnap.forEach((cinema) => {
        const cinemaObj: Cinema = { ...(cinema.data() as Cinema), id: cinema.id };
        const cinemaStrObj = JSON.stringify(cinemaObj);
        if (movie.availableOn) {
          const availableOn = getAvailableCinemas(movie.availableOn);
          if (availableOn.includes(cinemaObj.id)) {
            cinemasStrObjs.push(cinemaStrObj);
          }
        }
      });
      returnMovie = {
        props: {
          movieStrObj,
          cinemasStrObjs,
        },
        revalidate: 60,
      };
    }
  }

  return returnMovie;
};
